import { AlertCircle } from '@untitled-ui/icons-react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Modal } from '@myhealth-raccoon/components';
import { isRequestError, useAsyncData } from '@myhealth-raccoon/services';

import { OutstandingBillingCheck } from '../booking-flow/OutstandingBillingCheck';
import { PreBookingInformation } from '../booking-flow/PreBookingInformation';
import { InlineModal } from 'src/components/InlineModal';
import { Availability, AvailabilityTimeslotPayload } from 'src/components/availability';
import { AfterHoursAvailability } from 'src/components/availability/AfterHoursAvailability';
import { BulkBilledTelehealthEligibility } from 'src/components/forms/BulkBilledTelehealthEligibility';
import {
    BookWithClinicForm,
    BookWithDoctorForm,
    BookingInformationForm,
    WidgetFormRef,
} from 'src/components/forms/widget';
import { useMessageListener, usePostMessage } from 'src/contexts/message';
import { useAlertWarning } from 'src/hooks/useAlertWarning';
import { useClinicSelector } from 'src/hooks/useAsyncSelector';
import { useDebounce } from 'src/hooks/useDebounce';
import { useOpenApp } from 'src/hooks/useOpenApp';
import { useServiceTypeFilters } from 'src/hooks/useServiceTypeFilters';
import { VisitInfo, authService } from 'src/services/auth';
import { AppointmentType, FirstAvailableModel, availabilityService } from 'src/services/availability';
import { BookingServiceSource, BookingUserProfileModel, bookingService } from 'src/services/booking';
import { Customer } from 'src/services/customer/types.ts';
import { customerBookingService } from 'src/services/customerBooking';
import { MedicareCardStatus } from 'src/services/patient';
import { SiteBaseModel } from 'src/services/site';
import { umbracoService } from 'src/services/umbraco';
import { UserWithSiteModel } from 'src/services/user';
import { useDispatch } from 'src/store';
import { setBookingServiceSource, setClinicId } from 'src/store/appointment';
import { setCustomerId } from 'src/store/customer';
import { isFaceToFace, newPatientRegex } from 'src/utils/appointmentTypes';
import { selectedDoctorIsDisplayOnly } from 'src/utils/doctor';
import { firstAvailableToNumber } from 'src/utils/firstAvailable';
import { innerHtml } from 'src/utils/html';
import { clinicName, doctorName } from 'src/utils/name';
import { ConflictType, PerformConflictChecks, requiresEligibilityCheck } from 'src/utils/validateAppointmentType';

type Props = {
    className?: string;
    isClinicPage?: boolean;
    isModal?: boolean;
};

type CUSTOMER_BOOKING_STEP =
    | 'AVAILABILITY'
    | 'PATIENT_NOT_FOUND'
    | 'OUTSTANDING_BALANCE'
    | 'PHI'
    | 'BB_ELIGIBILITY'
    // 'next step' will decide dynamically what the next modal to display is
    | 'NEXT_STEP';

export function CustomerBookingFlow({ className, isClinicPage, isModal }: Props) {
    const [doctorDisplayName, setDoctorDisplayName] = useState<string>();
    const [doctorIsDisplayOnly, setDoctorIsDisplayOnly] = useState<boolean>(false);

    const {
        site,
        doctor: doctorSites,
        customer,
    } = useLoaderData() as {
        site?: SiteBaseModel;
        doctor?: UserWithSiteModel[];
        customer: Customer;
    };

    const widgetType = site ? 'site' : 'doctor';
    const [urlSearchParams] = useSearchParams();

    const referrer = urlSearchParams.get('referrer');

    const [bookingQuery, setValues] = useState<BookingInformationForm>();

    const postMessage = usePostMessage();

    const { data: umbracoSite } = useClinicSelector();
    const clinicId = site?.id ?? (bookingQuery?.siteId ? Number(bookingQuery.siteId) : doctorSites![0].siteId) ?? 0;
    const afterHoursEnabled = urlSearchParams.has('afterHours') && urlSearchParams.get('afterHours') === 'True';

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setBookingServiceSource(BookingServiceSource.PatientPortal));
    }, [clinicId, dispatch]);
    useEffect(() => {
        dispatch(setClinicId(clinicId));
    }, [clinicId, dispatch]);
    useEffect(() => {
        dispatch(setCustomerId(customer.id));
    }, [customer, dispatch]);

    const [appointmentTypeId, setAppointmentTypeId] = useState<string>();
    const [isAvailablityDisabled, setIsAvailablityDisabled] = useState(false);

    const [bookingStep, setBookingStep] = useState<CUSTOMER_BOOKING_STEP>('AVAILABILITY');

    const [billingEligibility, setBillingEligibility] = useState<{
        medicareStatus: MedicareCardStatus;
        hasOshc: boolean;
    }>();
    const [visitInfo, quietSetVisitInfo] = useState<VisitInfo>();
    const [didPassOutstandingBilling, setDidPassOutstandingBilling] = useState(false);
    const [selectedTimeslot, setSelectedTimeslot] = useState<AvailabilityTimeslotPayload>();
    const [medicareExcemptions, setMedicareExemptions] = useState<string[]>();

    const { alertCopy, closeAlert, isAlertOpen, openAlert } = useAlertWarning();

    const { data: appointmentTypes, ...getAppointmentTypes } = useAsyncData(
        {
            service: availabilityService,
            method: availabilityService.GetAppointmentTypes,
        },
        Number(clinicId)
    );

    const { data: doctors, ...getDoctors } = useAsyncData(
        {
            service: bookingService,
            method: bookingService.GetDoctorsAtSite,
        },
        Number(clinicId)
    );

    const {
        serviceType,
        setServiceType,
        afterHoursAppointmentTypes,
        specialAppointmentTypes,
        standardAppointmentTypes,
        currentSpecialAppointmentType,
    } = useServiceTypeFilters(
        umbracoSite?.clinicServiceTypes,
        appointmentTypes,
        urlSearchParams.get('defaultTab')?.trim().toLocaleUpperCase()
    );

    const initialValues: Partial<BookingInformationForm> | undefined = useMemo(() => {
        if (widgetType === 'site') {
            if (appointmentTypes) {
                const defaultAppointment = appointmentTypes.find((apt) => apt.isDefaultAppt) ?? appointmentTypes[0];
                return {
                    patientType: 'existing',
                    appointmentMode: isFaceToFace(defaultAppointment) ? 'faceToFace' : 'telehealth',
                    appointmentTypeId: defaultAppointment.appointmentTypeId,
                    doctorId: 'any',
                };
            }
        } else {
            const defaultSite =
                doctorSites!.find((s) => s.umbracoSiteId && s.umbracoSiteId.toString() === referrer) ?? doctorSites![0];
            return {
                siteId: defaultSite.siteId.toString(),
                appointmentMode: 'faceToFace',
                patientType: 'existing',
            };
        }
    }, [appointmentTypes, doctorSites, referrer, widgetType]);

    const changeValues = useDebounce(
        useCallback((newValues: BookingInformationForm) => {
            setValues({ ...newValues });
        }, []),
        300
    );

    const onValidate = useCallback((valid: boolean) => {
        setIsAvailablityDisabled(!valid);
    }, []);

    useEffect(() => {
        // Keep the personal details
        quietSetVisitInfo(undefined);
        setBillingEligibility(undefined);
    }, [umbracoSite]);

    const { openApp } = useOpenApp({
        useSameWindow: true,
        customerId: customer.id,
    });

    useMessageListener(
        useCallback(
            (payload) => {
                if (typeof payload === 'object' && payload.message === 'myhealth-booking-ui-after-hours') {
                    setServiceType(payload.value ? 'After Hours' : 'General Care');
                }
            },
            [setServiceType]
        )
    );

    const appointmentType = useMemo(
        () => appointmentTypes?.find((apt) => apt.appointmentTypeId === bookingQuery?.appointmentTypeId),
        [appointmentTypes, bookingQuery?.appointmentTypeId]
    );

    useEffect(() => {
        if (getAppointmentTypes.error) {
            postMessage({
                message: 'myhealth-booking-ui-fatal',
                value: getAppointmentTypes.error.message || getAppointmentTypes.error.name,
            });
        }
        if (getDoctors.error) {
            postMessage({
                message: 'myhealth-booking-ui-fatal',
                value: getDoctors.error.message || getDoctors.error.name,
            });
        }
    }, [getAppointmentTypes.error, getDoctors.error, postMessage]);

    const [isSubmittingPatientInfo, setIsSubmittingPatientInfo] = useState(false);

    const setVisitInfo = useCallback(
        async (clinicId: number, isNewPatient: boolean) => {
            setIsSubmittingPatientInfo(true);
            try {
                const { access_token } = await umbracoService.GetBookingToken();
                authService.Token = { jwt: access_token, token_type: 'umbraco' };
                await authService.ConnectBookingApp(clinicId, isNewPatient);
                const visitInfo = await customerBookingService.GetBookingVisitInfo(
                    customer.id,
                    clinicId,
                    !isNewPatient
                );
                quietSetVisitInfo(visitInfo);
                const raccoonToken = await customerBookingService.AuthoriseBookingForCustomer(customer.id, clinicId);
                authService.Token = { jwt: raccoonToken.access_token, token_type: 'booking_guest' };
            } finally {
                setIsSubmittingPatientInfo(false);
            }
        },
        [customer.id]
    );

    const {
        data: firstAvailable,
        isLoading: isLoadingFirstAvailable,
        invoke,
    } = useAsyncData(
        {
            service: availabilityService,
            method: availabilityService.GetFirstAvailable,
            selector: useCallback(
                (f: FirstAvailableModel[]) =>
                    f?.toSorted((a, b) => firstAvailableToNumber(a) - firstAvailableToNumber(b)) ?? [],
                []
            ),
        },
        clinicId,
        appointmentTypeId ?? '',
        null
    );

    const formRef = useRef<WidgetFormRef>(null);

    const assertValidAppointment = useCallback(
        (
            args: Partial<{
                customer: Customer;
                visitInfo: VisitInfo;
                appointmentType: AppointmentType;
                doctor: BookingUserProfileModel;
            }> = {}
        ) => {
            const innerVisitInfo = (args.visitInfo ?? visitInfo)!;
            if (!(args.appointmentType ?? appointmentType)!.newPatient && innerVisitInfo.isNewPatient) {
                toast.warning('Appointment type not available for new patients.');
                throw 'Appointment type not available for new patients';
            }
            if (
                (args.appointmentType ?? appointmentType)!.appointmentName.match(newPatientRegex) &&
                (args.appointmentType ?? appointmentType)!.newPatient &&
                !innerVisitInfo.isNewPatient
            ) {
                toast.warning('Appointment type not available for existing patients.');
                throw 'Appointment type not available for existing patients';
            }
            const conflictResult = PerformConflictChecks({
                appointmentType: args.appointmentType || appointmentType,
                patientDob: (args.customer ?? customer)!.dateOfBirth,
                doctor: args.doctor || doctors.find((dr) => dr.userId === selectedTimeslot?.doctorId.toString()),
                isNewPatient: innerVisitInfo.isNewPatient,
                lastF2FDate: innerVisitInfo.lastF2FDate,
                lastSeenDid: innerVisitInfo.lastSeenDid,
                bannedLevel: innerVisitInfo.bannedLevel,
            });
            if (!conflictResult.valid) {
                switch (conflictResult.conflictId) {
                    case ConflictType.doctorOwnPatientsOnly:
                        openAlert({
                            title: 'This provider only accepts patients they have previously seen',
                            body: `Please contact the centre to make an appointment on <a className="text-primary" href="tel:${umbracoSite?.phoneNumber}">${umbracoSite?.phoneNumber}</a> or select another provider.`,
                        });
                        break;
                    case ConflictType.doctorNoNewPatient:
                        openAlert({
                            title: 'This provider does not accept new patients',
                            body: `Please contact the centre to make an appointment on <a className="text-primary" href="tel:${umbracoSite?.phoneNumber}">${umbracoSite?.phoneNumber}</a> or select another provider.`,
                        });
                        break;
                    case ConflictType.patientBanned:
                        if (innerVisitInfo.bannedLevel?.includes('Level 1')) {
                            openAlert({
                                title: 'We are currently unable to schedule an appointment for you',
                                body: `Please call the practice on <a className="text-primary" href="tel:${umbracoSite?.phoneNumber}">${umbracoSite?.phoneNumber}</a> to speak with one of our friendly receptionists to book with your doctor.`,
                            });
                        } else if (innerVisitInfo.bannedLevel?.includes('Level 2')) {
                            openAlert({
                                title: 'We are currently unable to schedule an appointment for you.',
                                body: 'Please consult <a href="https://www.healthdirect.gov.au" target="_top">www.healthdirect.gov.au</a> for an alternative practice in the area.',
                            });
                        }
                        break;
                    default:
                        openAlert({
                            title: 'Appointment type unavailable',
                            body: 'Your eligibility could not be verified for this appointment type. Only patients within a certain age range, or identified section of our patient base, are currently eligible for this specific appointment type.',
                        });
                        break;
                }
                throw conflictResult.conflictId;
            }
        },
        [appointmentType, customer, doctors, openAlert, selectedTimeslot?.doctorId, umbracoSite?.phoneNumber, visitInfo]
    );

    const selectTimeslot = useCallback(
        async (appt: AvailabilityTimeslotPayload) => {
            setSelectedTimeslot(appt);
            try {
                if (!visitInfo) {
                    await setVisitInfo(appt.clinicId, bookingQuery?.patientType === 'new');
                }
                setBookingStep('NEXT_STEP');
            } catch (e) {
                if (isRequestError(e) && e.statusCode === 404) {
                    setBookingStep('PATIENT_NOT_FOUND');
                } else {
                    console.error(e);
                }
            }
        },
        [bookingQuery?.patientType, setVisitInfo, visitInfo]
    );

    const handlePatientNotFoundAction = useCallback(async () => {
        await setVisitInfo(clinicId, true);
        setBookingStep('NEXT_STEP');
    }, [clinicId, setVisitInfo]);

    const selectAfterHoursAppointmentType = useCallback((apptType: AppointmentType) => {
        // @ts-expect-error types strange
        setValues((currentValues) => ({
            ...currentValues,
            appointmentTypeId: apptType.appointmentTypeId,
        }));
    }, []);

    const submitPhi = useCallback(async (medicareStatus: MedicareCardStatus, hasOshc: boolean) => {
        setBillingEligibility({ medicareStatus, hasOshc });
        setBookingStep('NEXT_STEP');
    }, []);

    const savedPatient = useMemo(() => {
        if (customer && visitInfo) {
            return {
                displayName: `${customer.firstName} ${customer.lastName}`,
                dob: customer.dateOfBirth,
                visitInfo: visitInfo,
            };
        }
    }, [customer, visitInfo]);

    const submitExemptions = useCallback(async (_isEligible: boolean, exemptions: string[]) => {
        setMedicareExemptions(exemptions);
        setBookingStep('NEXT_STEP');
    }, []);

    const onEditDetails = useCallback(() => {
        setBookingStep('PHI');
    }, []);

    useEffect(() => {
        switch (bookingStep) {
            case 'AVAILABILITY':
                setSelectedTimeslot(undefined);
                return;
            case 'NEXT_STEP':
                if (!selectedTimeslot || !appointmentType || !visitInfo) {
                    setBookingStep('AVAILABILITY');
                    return;
                }

                try {
                    assertValidAppointment();
                } catch (e) {
                    setBookingStep('AVAILABILITY');
                    if (
                        e === 'Appointment type not available for new patients' ||
                        e === 'Appointment type not available for existing patients'
                    ) {
                        formRef.current?.openAppointmentTypeSelection();
                    }
                    if (
                        typeof e === 'number' &&
                        [ConflictType.lastVisitTooLongAgo, ConflictType.outOfAgeRange].includes(e)
                    ) {
                        formRef.current?.openAppointmentTypeSelection();
                    }
                    return;
                }

                if (!didPassOutstandingBilling && umbracoSite?.blockUnpaidBooking) {
                    setBookingStep('OUTSTANDING_BALANCE');
                    return;
                }
                if (!billingEligibility) {
                    setBookingStep('PHI');
                    return;
                }
                if (
                    !medicareExcemptions &&
                    requiresEligibilityCheck(appointmentType, visitInfo, billingEligibility.medicareStatus)
                ) {
                    setBookingStep('BB_ELIGIBILITY');
                    return;
                }
                openApp(
                    clinicId,
                    selectedTimeslot.doctorId,
                    selectedTimeslot.date,
                    selectedTimeslot.appointmentTypeId,
                    {
                        name: `${customer.firstName} ${customer.lastName}`,
                        dob: customer.dateOfBirth,
                    },
                    selectedTimeslot.timeSlot,
                    visitInfo,
                    billingEligibility.medicareStatus,
                    billingEligibility.hasOshc ? 'Overseas Student/Visitor Health Cover (OSHC / OVHC)' : undefined,
                    undefined,
                    medicareExcemptions
                );
                setBookingStep('AVAILABILITY');
                return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingStep]);

    useEffect(() => {
        setMedicareExemptions(undefined);
    }, [appointmentType]);

    return (
        <div
            className={classNames(
                'flex w-full flex-col overflow-auto rounded-xl bg-white',
                { 'rounded-b-none sm:rounded-b-xl': isModal },
                className
            )}
        >
            <div className="sticky top-0 z-10 flex flex-col gap-2 rounded-t-xl border-b bg-white pb-2 pt-4 sm:pb-4">
                {isClinicPage && (
                    <div className="flex flex-row items-start gap-2 px-4">
                        <Button
                            variant="outline"
                            color={serviceType === 'General Care' ? 'primary' : 'gray'}
                            onClick={() => setServiceType('General Care')}
                        >
                            General Care
                        </Button>
                        {specialAppointmentTypes.map(([label]) => (
                            <Button
                                key={label}
                                variant="outline"
                                color={serviceType === label ? 'primary' : 'gray'}
                                onClick={() => setServiceType(label)}
                            >
                                {label}
                            </Button>
                        ))}
                        {umbracoSite?.isAfterHoursServiceAvailable && (
                            <Button
                                variant="outline"
                                color={serviceType === 'After Hours' ? 'primary' : 'gray'}
                                onClick={() => setServiceType('After Hours')}
                            >
                                After Hours
                            </Button>
                        )}
                    </div>
                )}
                <h3 className="px-4">
                    <span className="block text-lg font-semibold text-gray-900">
                        Book with {doctorDisplayName ?? clinicName(site)}
                    </span>
                    <small className="mt-1 block text-sm font-light text-gray-600">
                        Your selections will help show the right availability.
                    </small>
                </h3>
            </div>
            {(serviceType === 'General Care' || serviceType === 'Cosmetic') && (
                <div className="space-y-4 pb-5">
                    {widgetType === 'site' ? (
                        <BookWithClinicForm
                            ref={formRef}
                            className=""
                            doctors={doctors}
                            appointmentTypes={
                                currentSpecialAppointmentType
                                    ? currentSpecialAppointmentType[1]
                                    : standardAppointmentTypes
                            }
                            onNewValues={changeValues}
                            onValidate={onValidate}
                            defaultValues={initialValues}
                            firstAvailable={firstAvailable}
                            isLoadingFirstAvailable={isLoadingFirstAvailable}
                            isLoadingAppointmentTypes={getAppointmentTypes.isLoading}
                            isLoadingDoctors={getDoctors.isLoading}
                            onChangeAppointmentType={setAppointmentTypeId}
                            patient={savedPatient}
                            onEditDetails={onEditDetails}
                        />
                    ) : (
                        <BookWithDoctorForm
                            ref={formRef}
                            clinics={doctorSites!}
                            onNewValues={changeValues}
                            onValidate={onValidate}
                            defaultValues={initialValues}
                            onDoctorDetails={(doc) => {
                                setDoctorDisplayName(doctorName(doc));
                                setDoctorIsDisplayOnly(doc.displayProfileNoOnlineBooking);
                            }}
                            patient={savedPatient}
                            onEditDetails={onEditDetails}
                        />
                    )}

                    {bookingQuery && appointmentType && bookingQuery.doctorId && <hr className="" />}

                    {bookingQuery &&
                        appointmentType &&
                        bookingQuery.doctorId &&
                        (bookingQuery.doctorId === 'any' ? (
                            <Availability
                                allUsers={doctors}
                                disabled={
                                    isAvailablityDisabled || bookingStep !== 'AVAILABILITY' || isSubmittingPatientInfo
                                }
                                appointmentType={appointmentType}
                                clinicId={Number(clinicId)}
                                onSelectTimeSlot={selectTimeslot}
                                futureApptWeeks={umbracoSite?.futureApptWeeks}
                                isLoadingFirstAvailable={isLoadingFirstAvailable}
                                firstAvailableDate={firstAvailable?.[0]?.firstAvailable?.date}
                                refreshAvailability={invoke}
                            />
                        ) : (
                            <Availability
                                errorElement={
                                    doctorIsDisplayOnly ||
                                    selectedDoctorIsDisplayOnly(bookingQuery.doctorId, doctors) ? (
                                        <>
                                            <p>
                                                <strong>This provider does not accept online bookings.</strong>
                                            </p>
                                            <p>
                                                Please select another provider or contact the centre to make an
                                                appointment on{' '}
                                                <a className="text-primary" href={`tel:${umbracoSite?.phoneNumber}`}>
                                                    {umbracoSite?.phoneNumber}
                                                </a>
                                                .
                                            </p>
                                        </>
                                    ) : undefined
                                }
                                disabled={
                                    isAvailablityDisabled ||
                                    selectedDoctorIsDisplayOnly(bookingQuery.doctorId, doctors) ||
                                    bookingStep !== 'AVAILABILITY' ||
                                    isSubmittingPatientInfo
                                }
                                doctorId={bookingQuery.doctorId}
                                appointmentType={appointmentType}
                                allUsers={doctors}
                                clinicId={Number(clinicId)}
                                onSelectTimeSlot={selectTimeslot}
                                futureApptWeeks={umbracoSite?.futureApptWeeks}
                                isLoadingFirstAvailable={isLoadingFirstAvailable}
                                firstAvailableDate={
                                    firstAvailable?.find((fa) => fa.user.userId.toString() === bookingQuery.doctorId)
                                        ?.firstAvailable?.date
                                }
                                refreshAvailability={invoke}
                            />
                        ))}
                </div>
            )}
            {serviceType === 'After Hours' &&
                umbracoSite &&
                (umbracoSite.isAfterHoursServiceAvailable ? (
                    <>
                        <div className="p-4 text-sm text-gray-700">
                            {!afterHoursEnabled && (
                                <span className="border-error-200 bg-error-50 text-error-700 mb-4 inline-block w-fit rounded-full px-2 py-0.5">
                                    <i className="bg-error-500 mr-2 inline-block size-1.5 rounded-full align-middle text-sm font-medium"></i>
                                    Currently unavailable while the clinic is open
                                </span>
                            )}
                            <h1 className="mb-1 text-lg font-semibold text-gray-900">
                                {umbracoSite.afterHoursServiceTitle || 'Book After-hours Care service'}
                            </h1>

                            {umbracoSite.afterHoursServiceContentHtml ? (
                                <div
                                    className="prose prose-sm"
                                    {...innerHtml(umbracoSite.afterHoursServiceContentHtml)}
                                />
                            ) : (
                                <>
                                    <p className="mb-[1em]">
                                        <strong className="font-medium">
                                            Phone service for existing patients for 'Urgent' matters that can't wait
                                            untill regular clinic hours. It is a privately charged service with no
                                            Medicare rebate.
                                        </strong>
                                    </p>
                                    <p>
                                        For medical emergencies that require immediate medical attention, please
                                        consider your local Hospital Emergency Department or calling '000".
                                    </p>
                                </>
                            )}
                        </div>
                        {afterHoursEnabled && (
                            <>
                                <hr />
                                <h2 className="bg-gray-50 px-4 py-2.5 text-lg font-medium text-gray-900">
                                    Select a timeslot
                                </h2>
                                <AfterHoursAvailability
                                    onSelectAppointmentType={selectAfterHoursAppointmentType}
                                    siteId={clinicId}
                                    className="p-4"
                                    appointmentTypes={afterHoursAppointmentTypes}
                                    onSelectTimeSlot={selectTimeslot}
                                    allUsers={doctors}
                                    disabled={bookingStep !== 'AVAILABILITY' || isSubmittingPatientInfo}
                                />
                            </>
                        )}
                    </>
                ) : (
                    <div className="p-6 text-sm text-gray-700">
                        <h1 className="mb-1 text-lg font-semibold text-gray-900">
                            {umbracoSite.afterHoursServiceTitle || 'Book After-hours Care service'}
                        </h1>
                        <div
                            className="prose prose-sm"
                            {...innerHtml(umbracoSite.afterHoursServiceDisabledContentHtml)}
                        />
                    </div>
                ))}

            <InlineModal
                isOpen={bookingStep === 'PATIENT_NOT_FOUND'}
                onClose={() => setBookingStep('AVAILABILITY')}
                size="md"
                title={
                    <h2 className="text-3xl font-semibold text-gray-900">
                        We couldn't find a matching patient record.
                    </h2>
                }
            >
                <div className="prose prose-sm px-5">
                    <p>
                        We couldn’t find a match for the details you provided in our active patient records. Please
                        ensure that your <strong>name, date of birth, and mobile number</strong> match exactly as they
                        were entered during registration and try again.
                    </p>
                    <p>
                        If you don’t have an existing record, you can proceed as a <strong>new patient</strong>.
                        Alternatively, contact the clinic directly for assistance.
                    </p>
                </div>
                <div className="my-5 flex flex-row justify-end gap-3 px-5">
                    <Button color="gray" type="button" onClick={() => setBookingStep('AVAILABILITY')}>
                        Back
                    </Button>
                    <Button
                        color="primary"
                        type="button"
                        onClick={handlePatientNotFoundAction}
                        disabled={isSubmittingPatientInfo}
                    >
                        Continue as New Patient
                    </Button>
                </div>
            </InlineModal>
            {bookingStep === 'OUTSTANDING_BALANCE' && (
                <OutstandingBillingCheck
                    onSuccess={() => {
                        setDidPassOutstandingBilling(true);
                        setBookingStep('NEXT_STEP');
                    }}
                    onCancel={() => setBookingStep('AVAILABILITY')}
                />
            )}
            <PreBookingInformation
                isOpen={bookingStep === 'PHI'}
                firstShown={!billingEligibility}
                onCancel={() => {
                    setBookingStep('AVAILABILITY');
                }}
                onContinue={submitPhi}
                isNewPatient={false}
                myMedicareRegisterPath={
                    site && site.automedSiteId
                        ? `${import.meta.env.VITE_AUTOMED_URL}/online/mymedicare/${site?.automedSiteId}/loc/${site.bpLocationId}`
                        : undefined
                }
            />
            <BulkBilledTelehealthEligibility
                isOpen={bookingStep === 'BB_ELIGIBILITY'}
                onClose={() => setBookingStep('AVAILABILITY')}
                visitInfo={visitInfo}
                validMedicare={billingEligibility?.medicareStatus === MedicareCardStatus.Valid}
                onSubmit={submitExemptions}
            />

            <Modal size="sm" open={isAlertOpen} onClose={closeAlert} icon={{ icon: AlertCircle, color: 'warning' }}>
                {alertCopy && (
                    <div className="prose">
                        <h4>{alertCopy.title}</h4>
                        <p {...innerHtml(alertCopy.body)} />
                    </div>
                )}
                <Button color="gray" className="mt-8 w-full" onClick={closeAlert}>
                    Close
                </Button>
            </Modal>
        </div>
    );
}
